import { Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * "Already on MPS" notice, shown when an upload collides with something the
 * catalogue already holds.
 *
 * Why a panel rather than the usual snackbar: a snackbar times out, holds one
 * line, and cannot carry an action. The thing the user needs here is the
 * OPPOSITE — what already exists, why it collided, and what to do instead. On a
 * bulk upload that is a list, not a sentence, so it stays on screen until it is
 * dismissed. This follows the pattern catalogue tooling settles on: flag the
 * duplicate before it reaches the catalogue, show the conflicting record, and
 * offer the resolution rather than silently dropping the row.
 *
 * For a supplier the resolution is always the same — the part is already on
 * MPS, so it is theirs to ASSIGN, not to upload again. Uploading a second copy
 * would split buyer demand across two identical rows.
 */
@Component({
  selector: 'app-duplicate-alert',
  templateUrl: './duplicate-alert.component.html',
  styleUrls: ['./duplicate-alert.component.css'],
})
export class DuplicateAlertComponent {
  /** Items that were refused, each with the reason it collided. */
  @Input() duplicates: { name: string; reason: string }[] = [];

  /** Heading; defaults to a count-aware line when not supplied. */
  @Input() title = '';

  /** Show the "assign it instead" action (suppliers only). */
  @Input() canAssign = false;

  @Output() dismiss = new EventEmitter<void>();
  @Output() assign = new EventEmitter<void>();

  get headline(): string {
    if (this.title) return this.title;
    const n = this.duplicates?.length || 0;
    return n === 1
      ? 'This spare part is already on MPS'
      : `${n} items are already on MPS`;
  }
}
